import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import { useEntityOptions } from '../hooks/useEntityOptions';
import { EntitySelect } from '../components/shared/EntitySelect';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, CheckSquare, GripVertical, Calendar, Pencil, Trash2, User
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const TASK_COLUMNS = [
  { id: 'todo', label: 'To Do', color: '#6B7280', bg: '#F9FAFB' },
  { id: 'in_progress', label: 'In Progress', color: '#3B82F6', bg: '#EFF6FF' },
  { id: 'review', label: 'In Review', color: '#F59E0B', bg: '#FFFBEB' },
  { id: 'done', label: 'Done', color: '#10B981', bg: '#ECFDF5' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-600' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
];

function TaskModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ title: '', description: '', status: 'todo', priority: 'medium', assigned_to: '', due_date: '' });
  const [saving, setSaving] = useState(false);
  const { employees } = useEntityOptions();
  useEffect(() => {
    if (editing) setForm({ title: editing.title || '', description: editing.description || '', status: editing.status || 'todo', priority: editing.priority || 'medium', assigned_to: editing.assigned_to || '', due_date: editing.due_date || '' });
    else setForm({ title: '', description: '', status: 'todo', priority: 'medium', assigned_to: '', due_date: '' });
  }, [editing, open]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const { data } = editing
        ? await axios.put(`${API}/api/operations/tasks/${editing.id}`, form)
        : await axios.post(`${API}/api/operations/tasks`, form);
      toast.success(editing ? 'Task updated' : 'Task created');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg"><DialogHeader><DialogTitle className="flex items-center gap-2"><CheckSquare className="h-5 w-5 text-blue-500" />{editing ? 'Edit' : 'New'} Task</DialogTitle><DialogDescription>Manage team tasks and assignments</DialogDescription></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Task title..." data-testid="task-title" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Status</Label><Select value={form.status} onValueChange={v => setForm(p => ({...p, status: v}))}><SelectTrigger data-testid="task-status"><SelectValue /></SelectTrigger><SelectContent>{TASK_COLUMNS.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Priority</Label><Select value={form.priority} onValueChange={v => setForm(p => ({...p, priority: v}))}><SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Assigned To</Label><EntitySelect value={form.assigned_to} onChange={v => setForm(p => ({...p, assigned_to: v}))} options={employees} placeholder="Select or type employee name" listId="task-employees" inputProps={{ "data-testid": "task-assigned" }} /></div>
            <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm(p => ({...p, due_date: e.target.value}))} data-testid="task-due" /></div>
          </div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-task-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Add Task'}</Button></DialogFooter>
        </form></DialogContent></Dialog>
  );
}

function TaskCard({ task, onClick, onDragStart }) {
  const pri = PRIORITIES.find(p => p.value === task.priority) || PRIORITIES[1];
  return (
    <div draggable onDragStart={e => onDragStart(e, task.id)} onClick={() => onClick(task)} className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:shadow-md transition-all group" data-testid={`task-card-${task.id}`}>
      <p className="text-sm font-medium text-gray-900 mb-1.5 line-clamp-2">{task.title}</p>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className={`${pri.color} text-[9px] px-1.5`}>{pri.label}</Badge>
        {task.assigned_to && <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><User className="h-2.5 w-2.5" /> {task.assigned_to}</span>}
        {task.due_date && <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Calendar className="h-2.5 w-2.5" /> {task.due_date}</span>}
      </div>
    </div>
  );
}

export default function TaskManagerPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);

  const fetchTasks = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/operations/tasks`); setTasks(data); }
    catch { toast.error('Failed to load tasks'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleSaved = (task, isUpdate) => { if (isUpdate) setTasks(p => p.map(t => t.id === task.id ? task : t)); else setTasks(p => [task, ...p]); };
  const handleDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', id); };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || dragId;
    if (!id) return;
    const task = tasks.find(t => t.id === id);
    if (!task || task.status === newStatus) return;
    setTasks(p => p.map(t => t.id === id ? { ...t, status: newStatus } : t));
    try { await axios.put(`${API}/api/operations/tasks/${id}`, { status: newStatus }); toast.success(`Moved to ${TASK_COLUMNS.find(c => c.id === newStatus)?.label}`); }
    catch { setTasks(p => p.map(t => t.id === id ? { ...t, status: task.status } : t)); toast.error('Failed'); }
    setDragId(null);
  };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="task-manager-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1"><div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><CheckSquare className="h-5 w-5 text-blue-500" /></div><div><h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Task Manager<PageInfoTooltip pageKey="tasks" /></h1><p className="text-xs text-gray-400">{tasks.length} tasks · {tasks.filter(t => t.status === 'done').length} done</p></div></div>
        <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 h-9 gap-1.5" data-testid="add-task-btn"><Plus className="h-4 w-4" /> New Task</Button>
      </div>

      {loading ? <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" /></div> : (
        <div className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4" data-testid="task-board">
          {TASK_COLUMNS.map(col => {
            const colTasks = tasks.filter(t => t.status === col.id);
            return (
              <div key={col.id} className="flex flex-col min-w-[260px] w-[260px] flex-shrink-0" onDragOver={handleDragOver} onDrop={e => handleDrop(e, col.id)} data-testid={`task-column-${col.id}`}>
                <div className="flex items-center gap-2 mb-3 px-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: col.color }} />
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">{col.label}</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 ml-auto">{colTasks.length}</Badge>
                </div>
                <div className="flex-1 space-y-2 p-1 rounded-lg min-h-[200px]" style={{ backgroundColor: col.bg }}>
                  {colTasks.map(task => <TaskCard key={task.id} task={task} onClick={t => { setEditing(t); setModalOpen(true); }} onDragStart={handleDragStart} />)}
                  {colTasks.length === 0 && <p className="text-xs text-gray-400 text-center py-8">No tasks</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <TaskModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} />
    </div>
  );
}
